import {
  DigitalDataEvent,
  MetricsTrackerConfigurationType,
  MetricsTrackerConfigurationUserType,
  MetricsTrackerConfigurationUserUpdateableType,
} from '../types/types';

const ANONYMOUS_USER_ID = '1';

// A dict of user properties from the user object to send as user properties
// and what they should be sent to amplitude as
type MetricsTrackerConfigurationUserTypeKeys = keyof MetricsTrackerConfigurationUserType &
  keyof MetricsTrackerConfigurationUserUpdateableType;
const CORE_USER_PROPS: Partial<Record<
  MetricsTrackerConfigurationUserTypeKeys,
  string
>> = {
  package: 'userPackageId',
  packageName: 'userPackageName',
  subscriptionStatus: 'userSubscriptionStatus',
};
const CORE_USER_PROPS_KEYS = Object.keys(
  CORE_USER_PROPS
) as MetricsTrackerConfigurationUserTypeKeys[];

export default function amplitudeSubscriber(
  digitalDataEvent: DigitalDataEvent,
  config: MetricsTrackerConfigurationType
) {
  const { amplitudeToken, user } = config;
  const { data: digitalData } = digitalDataEvent;
  const { amplitudeEvent, actionType, actionFlow, ...data } = digitalData;

  if (
    // bail if we have no ampltude token
    !amplitudeToken ||
    // or if this is not an amplitude event
    !amplitudeEvent
  ) {
    return;
  }

  const { id: userId, isAuthenticated, teamId, languageId } = user;

  // we'll include the amplitude SDK here via "require" to avoid getting
  // yelled at by the test runner since amplitude makes explicit references
  // to "window", which doesn't exist in the test environment before we mock it
  // eslint-disable-next-line global-require
  const amplitudeRequire = require('amplitude-js');

  // this is to catch a bundling issue that was introduced with amplitude-js@8.11.0
  // that used the ESM module instead of the default `main` import:
  // https://github.com/amplitude/Amplitude-JavaScript/commit/3e98d506f003ba428ab08796d1246ccb387d1f32
  const amplitude = amplitudeRequire.default ?? amplitudeRequire;

  try {
    const amplitudeInstance = amplitude.getInstance();
    amplitudeInstance.init(amplitudeToken, null, { includeReferrer: true });

    if (isAuthenticated) {
      // if the user belongs to a team, send that down as a group property
      // set this before the user props below so that group info is sent as part
      // of the user props call
      if (teamId) {
        amplitudeInstance.setGroup('teamId', teamId);
      }

      amplitudeInstance.setUserId(userId);

      // add in any core props if they're present on the user object
      const userProps = { languageId };
      CORE_USER_PROPS_KEYS.forEach(key => {
        const val = user[key];
        if (val && CORE_USER_PROPS[key]) {
          userProps[CORE_USER_PROPS[key]] = user[key];
        }
      });
      amplitudeInstance.setUserProperties(userProps);
    } else {
      // make sure we've cleared out the data for any previous logged in user
      amplitudeInstance.clearStorage();
      amplitudeInstance.setUserId(ANONYMOUS_USER_ID);
    }

    amplitudeInstance.logEvent(amplitudeEvent, { ...data });
  } catch (err) {
    // silently fail; we don't want a bad metrics call taking the whole experience down
  }
}
